import React, { useState } from "react";
import {
  Typography,
  Box,
  Grid,
  Avatar,
  Paper,
  Chip,
  Card,
  Stack,
} from "@mui/material";
import Lightbox from "react-awesome-lightbox";
import * as settings from "../settings";

const images = [
  `${settings.BASEURL}/media/matthew1.png`,
  `${settings.BASEURL}/media/matthew2.png`,
];

const skills = {
  "Representative Systems": [
    "Built a CRM-style lead management system integrating Twilio, external data sources, and automated follow-up workflows, supporting outbound calling and real-time agent activity tracking",
    "Designed and deployed a distributed operations platform supporting remote workforce management, including real-time task routing, scheduling, and automated communication workflows",
    "Developed a transaction-based system integrating payments, messaging, and scheduling across external services via REST APIs and webhooks",
  ],
  "Domains & System Types": [
    "Lead generation and outreach systems",
    "Telephony and messaging workflows (Twilio, SIP, Asterisk)",
    "Service and workforce management tools",
    "Internal operational dashboards and portals",
  ],
  "Systems Architecture & Engineering": [
    "Defined system architecture across application, data, and integration layers",
    "Evaluated tradeoffs between performance and maintainability when designing system architectures",
    "Designed systems supporting inventory, dispatching, and operational workflows",
    "Established system boundaries, interfaces, and data flows",
    "Designed systems within real-world constraints including performance, cost, and operational complexity",
    "System Design (Conceptual, Logical, Physical)",
    "Solution & Integration Architecture",
    "Technical Requirements Translation (Business → Engineering)",
  ],
  "System Ownership & Collaboration": [
    "Independently designed, built, and maintained systems from initial concept through production deployment",
    "Configured and administered core business applications (Salesforce), managing user permissions, workflows, and operational rules",
    "Responsible for ongoing system performance and improvements",
    "Evolved and refactored systems over time to support changing business requirements",
    "Collaborated with stakeholders across business and technical teams",
    "Translated business requirements into technical specifications",
  ],
  "Business Process & SDLC": [
    "Participated in full SDLC including requirements, design, testing, deployment, and support",
    "Worked in iterative environments with continuous delivery and process improvement",
    "Supported system evolution and change management processes, ensuring stability during feature updates",
    "Analyzed business processes to identify automation opportunities",
    "Optimized workflows to improve operational efficiency and reduce manual effort",
    "Conducted system testing (functional, integration, and user acceptance)",
    "Created and maintained technical and functional documentation",
  ],
  "APIs & Integrations": [
    "Managed complex integration scenarios involving multiple systems with differing data models, authentication methods, and operational constraints",
    "Designed and managed integrations across multiple internal and external systems",
    "Integrated deeply with external platforms, aligning data structures with CRM system models",
    "Handled data synchronization, transformation, and consistency across platforms",
    "Ensured reliability and fault tolerance in integration pipelines",
    "REST APIs (design, implementation, consumption)",
    "Webhooks (event-driven integrations)",
    "API Design (versioning, authentication, reliability)",
    "Data Mapping & Transformation (cross-system alignment)",
  ],
  "Databases & Data Engineering": [
    "Designed relational data models aligned with business workflows",
    "Experience working with structured and semi-structured data across operational systems",
    "Supported data migration and system transition efforts, including schema evolution and cross-system data alignment",
    "PostgreSQL / MySQL (production relational systems)",
    "MongoDB (flexible schema applications)",
    "Redis (caching, queueing, performance optimization)",
    "Relational Data Modeling (normalized schemas, relationships)",
    "Query Optimization (performance tuning, indexing)",
  ],
  "Data & Analytics": [
    "Built reporting and analytics layers for operational insights",
    "Worked with customer lifecycle data models and reporting structures similar to CRM platforms",
    "Performed data analysis to identify system inefficiencies and optimization opportunities",
    "SQL Reporting (operational insights, complex analytical queries)",
    "Business Intelligence & Data Visualization (Power BI, Tableau)",
    "Data Analysis (debugging, business insights, optimization)",
    "Dashboarding (custom reporting interfaces)",
    "Performance Metrics (tracking KPIs, system efficiency)",
  ],
  "Security & Governance": [
    "Implemented role-based access control and permission systems",
    "Ensured system governance, auditability, and security best practices",
    "Worked with sensitive operational and financial data in secure environments",
    "Maintained system documentation and processes for operational and data workflows",
    "Access Control (role-based systems, permissions)",
    "Data Security (secure storage, transmission, authentication)",
  ],
  "DevOps & Reliability": [
    "Ensured system reliability, uptime, and strict process alignment",
    "Monitored system performance and resolved live production issues",
    "Implemented CI/CD pipelines for automated deployment",
    "Used logging and monitoring tools for diagnostics and alerting",
    "CI/CD Pipelines (Git-based automated deployments)",
    "Production Support (live systems, uptime responsibility)",
    "Incident Management (root cause analysis, rapid resolution)",
    "Performance Optimization (database + application layers)",
  ],
  "Cloud & Infrastructure": [
    "AWS (EC2, S3, RDS - production deployments)",
    "Google Cloud (compute, storage, services)",
    "Cloud Architecture (SMB to mid-scale systems)",
    "Docker (containerized deployments)",
    "Linux (server administration, scripting)",
  ],
  "Automation & Workflow Engineering": [
    "Business Process Automation (reducing manual operations)",
    "Workflow Design (multi-step business logic systems)",
    "System Orchestration (coordinating services and tasks)",
  ],
  "General Skills": [
    "Programming/Scripting Languages: Python, JavaScript, PHP, C, C++, MicroPython, Shell Scripts, Batch Scripts, TypeScript",
    "Frameworks & Libraries: React, React Native, Node JS, Flask, Express, Material UI, Bootstrap, Redux",
    "Databases & Backends: PostgreSQL, MySQL, Asterisk, Postfix, Dovecot",
    "Tools & Platforms: Docker, Containerization, Virtual Machines, Git, cURL",
    "Web Technologies: HTML5, CSS, Apache",
    "Generative AI: OpenAI, Locally Run AI Models, Ollama",
    "Operating Systems: Windows OS, Debian Based Linux Distros",
    "Payment Processing: Credit Card Processing, Stripe, Square",
    "Web Services and APIs: REST, Twilio, Sendgrid, Zapier, Hubspot, Mailgun, Mailchimp, PayPal, AWS, Google Cloud, Google Maps, OpenStreetMap, Zoom Video",
  ],
};

const AboutMePage = () => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Grid container spacing={4}>
          {/* LEFT SIDE */}
          <Grid item xs={12} md={6}>
            <Box textAlign="center" mb={4}>
              <Avatar
                alt="Matthew Kontos"
                src={`${settings.BASEURL}/media/matthew.png`}
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  border: "4px solid #0d47a1",
                }}
              />

              <Typography variant="h4" fontWeight="bold" color="primary">
                Matthew Kontos
              </Typography>

              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Business Systems Engineer | Systems Architecture | Automation
              </Typography>
            </Box>

            <Typography paragraph>
              I design and build systems that automate operations, reduce manual work, and help businesses run more efficiently.
            </Typography>

            <Typography paragraph>
              My work focuses on building end-to-end applications that connect APIs, automate workflows, and support day-to-day operations like lead management, communications, and service coordination. I’ve built systems for outbound calling, messaging, scheduling, and internal tools that replace manual processes and improve throughput.
            </Typography>

            <Typography paragraph>
              I specialize in automation for lead generation and service operations, including telephony systems, CRM integrations, and workflow automation. These systems handle real-time activity, background processing, and ongoing data synchronization to keep operations running smoothly.
            </Typography>

            <Typography paragraph>
              I’ve independently designed, built, and maintained systems from initial concept through production, taking full ownership of performance, reliability, and ongoing improvements.
            </Typography>

            <Typography paragraph>
              My experience covers the full development lifecycle, including requirements, system design, testing, deployment, and production support. I work closely with stakeholders to turn business processes into scalable systems that are practical, maintainable, and aligned with real operational needs.
            </Typography>
          </Grid>

          {/* RIGHT SIDE */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              Skills
            </Typography>

            {Object.entries(skills).map(([category, items]) => (
              <Box key={category} mb={3}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="primary"
                  textTransform="uppercase"
                  letterSpacing={1}
                  mb={1}
                  sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 0.5 }}
                >
                  {category}
                </Typography>

                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                  {items.map((skill) => {
                    const colonIndex = skill.indexOf(":");
                    if (colonIndex !== -1 && colonIndex < 40) {
                      const prefix = skill.substring(0, colonIndex);
                      const rest = skill.substring(colonIndex + 1);
                      return (
                        <Typography
                          component="li"
                          variant="body2"
                          key={skill}
                          sx={{ mb: 0.5 }}
                        >
                          <strong>{prefix}:</strong>{rest}
                        </Typography>
                      );
                    }
                    return (
                      <Typography
                        component="li"
                        variant="body2"
                        key={skill}
                        sx={{ mb: 0.5 }}
                      >
                        {skill}
                      </Typography>
                    );
                  })}
                </Box>
              </Box>
            ))}

            {/* Screenshots */}
            {images.length > 0 && (
              <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {images.map((img, i) => (
                    <Box
                      key={i}
                      component="img"
                      src={img}
                      alt={`Screenshot ${i + 1}`}
                      onClick={() => handleImageClick(i)}
                      sx={{
                        width: 160,
                        height: 90,
                        objectFit: "cover",
                        cursor: "pointer",
                        borderRadius: 1,
                        boxShadow: 2,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            )}
          </Grid>
        </Grid>
      </Paper>

      {isLightboxOpen && (
        <Lightbox
          images={images.map((src) => ({ url: src }))}
          startIndex={lightboxIndex}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </Box>
  );
};

export default AboutMePage;